import { useRef, type CSSProperties, type ReactNode } from "react";
import { Link } from "react-router";
import { posterFrameUrl } from "../content-calendar/videoThumbnails";
import BrandCore, { BrandCoreSkeleton } from "./BrandCore";
import RemindButton from "./RemindButton";
import type { HomeCallAction, HomeData, HomeMonthBucket, HomeState } from "./home.types";
import { NEEDS_SHOWN } from "./homeSince";
import { useArrival, useCountUp } from "./homeArrival";
import "./home.css";

/**
 * HOME (Figma 11 · Agency · The morning, 11:9032).
 *
 *   Band       since-your-last-visit sentence, four counters, the command
 *              centre (composer, project, Quick Create) and the brand core.
 *   Columns    Needs you · The month | What the brand learned
 *   Columns 2  Latest | Trend of the week
 *
 * The page takes its readings as one prop (`useHomeData` live, fixtures in the
 * harness). The composer is a control with its own hooks, handed in as a slot.
 * A figure that could not be read draws a dash, never 0. Until every read has
 * answered, the page is one skeleton in its own layout (HomeSkeleton).
 */

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

function SectionHead({ id, title, count, right }: { id: string; title: string; count?: number | null; right?: ReactNode }) {
  return (
    <div className="home-head">
      <h2 id={id} className="home-head-t">
        {title}
      </h2>
      {count != null ? <span className="home-head-n">{count}</span> : null}
      <span className="home-head-push" />
      {right}
    </div>
  );
}

/** M05: counts up in its turn on an arrival; a zero stays dim; a later
 *  change swaps in with a fade (keyed on the value). */
function Counter({ value, label, index }: { value: number | null; label: string; index: number }) {
  const { shown, counting } = useCountUp(value, index);
  return (
    <div className="home-counter">
      {/* Keyed on the value, so a later change (a poll) swaps in with a fade;
          the count itself never remounts it. */}
      <span key={String(value)} className="home-counter-swap">
        <b className={`home-counter-n${shown === 0 ? " home-counter-n--zero" : ""}`} data-counting={counting || undefined}>
          {shown ?? "–"}
        </b>
      </span>
      <span className="home-counter-l">{label}</span>
    </div>
  );
}

function Quiet({ children }: { children: ReactNode }) {
  return <p className="home-quiet">{children}</p>;
}

/* ------------------------------------------------------------------ band */

function Band({ data, composer }: { data: HomeData; composer: ReactNode }) {
  const { standing, counters, core, brand } = data;
  const factsLabel = counters.factsNew ? `FACTS · ${counters.factsNew} NEW` : "FACTS";
  return (
    <section className="home-band" aria-labelledby="home-line">
      <div className="home-band-left">
        <div className="home-words">
          <div className="home-standing">
            <p className="home-since">{standing.sinceLabel} · ON THIS DEVICE</p>
            <h1 id="home-line" className="home-line">
              {standing.line ?? "What changed could not be read."}
            </h1>
          </div>
          <div className="home-counters">
            <Counter index={0} value={counters.liveThisMonth} label="LIVE THIS MONTH" />
            <Counter index={1} value={counters.needYou} label="NEED YOU" />
            <Counter index={2} value={counters.running} label="RUNNING" />
            <Counter index={3} value={counters.facts} label={factsLabel} />
          </div>
        </div>
        {composer}
      </div>
      <Link to="/dashboard/brand-universe" className="home-universe" aria-label={`Open the Universe for ${brand.name}`}>
        <BrandCore core={core} initial={brand.initial} breathing={(counters.running ?? 0) > 0} />
        <span className="home-universe-cap">
          {core
            ? [
                brand.name,
                `${core.total} ${plural(core.total, "fact", "facts")}${core.newCount ? `, ${core.newCount} new` : ""}`,
                "open the Universe",
              ].join(" · ")
            : `${brand.name} · open the Universe`}
        </span>
      </Link>
    </section>
  );
}

/* ------------------------------------------------------------- needs you */

function CallAction({ action, primary }: { action: HomeCallAction; primary: boolean }) {
  const className = primary ? "home-action co-glass-control" : "home-second";
  if (action.kind === "remind") {
    return <RemindButton className={className} planId={action.planId} round={action.round} roundId={action.roundId} label={action.label} />;
  }
  return (
    <Link to={action.href} className={className}>
      {action.label}
    </Link>
  );
}

function NeedsYou({ needs }: { needs: HomeData["needs"] }) {
  const shown = needs.items.slice(0, NEEDS_SHOWN);
  const more = needs.items.length - shown.length;
  return (
    <section className="home-sec" aria-labelledby="home-needs">
      <SectionHead id="home-needs" title="Needs you" count={needs.total} />
      {needs.total === null && shown.length === 0 ? (
        <Quiet>What needs you could not be read.</Quiet>
      ) : shown.length === 0 ? (
        <Quiet>Nothing needs you.</Quiet>
      ) : (
        <ul className="home-list">
          {shown.map((call, i) => (
            <li key={call.id} className="home-call" style={{ "--n": i } as CSSProperties}>
              <i className="home-call-dot" aria-hidden="true" />
              <span className="home-call-text">
                <span className="home-call-t">{call.title}</span>
                {call.meta ? <span className="home-meta">{call.meta}</span> : null}
              </span>
              {call.second ? <CallAction action={call.second} primary={false} /> : null}
              <CallAction action={call.action} primary />
            </li>
          ))}
        </ul>
      )}
      {more > 0 ? (
        <Link to="/dashboard/content-calendar" className="home-more">
          {more} more in the calendar
        </Link>
      ) : null}
    </section>
  );
}

/* ------------------------------------------------------------- the month */

const BUCKET_LABEL: Record<HomeMonthBucket, string> = {
  not_started: "Not started",
  planning: "Planning",
  with_client: "With the client",
  in_production: "In production",
  live: "Live",
};

function TheMonth({ months, read }: { months: HomeData["months"]; read: boolean }) {
  return (
    <section className="home-sec" aria-labelledby="home-month">
      <SectionHead
        id="home-month"
        title="The month"
        right={
          <Link to="/dashboard/content-calendar" className="home-head-link">
            Open the calendar
          </Link>
        }
      />
      {!read ? (
        <Quiet>The calendar could not be read.</Quiet>
      ) : (
        months.map((m) => (
          <div key={m.key} className="home-month">
            <div className="home-month-head">
              <span className="home-month-name">{m.name}</span>
              <span className="home-month-state">{m.state}</span>
              <span className="home-head-push" />
              {m.right ? <span className="home-month-right">{m.right}</span> : null}
            </div>
            {m.sentence ? <p className="home-month-phases">{m.sentence}</p> : null}
            {m.segments.length > 0 ? (
              <div className="home-bar" role="img" aria-label={m.sentence}>
                {m.segments.map((s, i) => (
                  <span
                    key={s.bucket}
                    className={`home-bar-seg home-bar-seg--${s.bucket}`}
                    style={{ flexGrow: s.count, "--n": i } as CSSProperties}
                    title={`${BUCKET_LABEL[s.bucket]}: ${s.count}`}
                  />
                ))}
              </div>
            ) : null}
          </div>
        ))
      )}
    </section>
  );
}

/* ------------------------------------------------------------- learned */

function Learned({ rows, read }: { rows: HomeData["learned"]; read: boolean }) {
  return (
    <section className="home-sec" aria-labelledby="home-learned">
      <SectionHead id="home-learned" title="What the brand learned" />
      {rows.length === 0 ? (
        <Quiet>{read ? "Nothing learned yet. Facts and accepted signals land here." : "The universe could not be read."}</Quiet>
      ) : (
        <ul className="home-list">
          {rows.map((row) => (
            <li key={row.id}>
              <Link to={row.href} className="home-learned">
                <span className={`home-kind${row.kind === "signal" ? " home-kind--signal" : ""}`}>
                  {row.kind === "signal" ? "SIGNAL" : "FACT"}
                </span>
                <span className="home-learned-words">
                  <span className="home-learned-t">{row.text}</span>
                  <span className="home-meta">{row.meta}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ------------------------------------------------------------- latest */

function Latest({ rows, read }: { rows: HomeData["latest"]; read: boolean }) {
  return (
    <section className="home-sec" aria-labelledby="home-latest">
      <SectionHead id="home-latest" title="Latest" />
      {rows.length === 0 ? (
        <Quiet>{read ? "Nothing has happened here yet." : "The latest could not be read."}</Quiet>
      ) : (
        <ul className="home-list home-list--plain">
          {rows.map((row, i) => (
            <li key={row.id} className="home-activity">
              <span className="home-activity-age">{row.age}</span>
              <i className={`home-activity-dot${i === 0 ? " home-activity-dot--new" : ""}`} aria-hidden="true" />
              <span className="home-activity-t">{row.text}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ------------------------------------------------------------- trend */

function TrendOfWeek({ trend, read }: { trend: HomeData["trend"]; read: boolean }) {
  return (
    <section className="home-sec" aria-labelledby="home-trend">
      <SectionHead id="home-trend" title="Trend of the week" />
      {!trend ? (
        <Quiet>{read ? "A trend appears here once a discovery run lands one." : "The trends library could not be read."}</Quiet>
      ) : (
        <div className="home-trend">
          <Link to={trend.href} className="home-trend-media" aria-label={trend.title}>
            {trend.videoUrl ? (
              <video src={posterFrameUrl(trend.videoUrl)} muted playsInline preload="metadata" disablePictureInPicture />
            ) : null}
          </Link>
          <div className="home-trend-text">
            <p className="home-trend-meta">{trend.meta}</p>
            <Link to={trend.href} className="home-trend-t">
              {trend.title}
            </Link>
            <p className="home-trend-b">{trend.body}</p>
            <p className="home-trend-src">From the trends library</p>
            <Link to="/dashboard/workshop" state={{ draftPrompt: trend.recreatePrompt }} className="home-second home-trend-go">
              Recreate
            </Link>
          </div>
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------- loading */

/* The skeleton is the page's own layout (the same classes) with each word as a
   bar in its line box, so nothing moves when the readings land. Widths are the
   frame's (11:9032). */

function Sk({ kind, w }: { kind: string; w?: number | string }) {
  return <span className={`home-sk home-sk--${kind}`} style={w === undefined ? undefined : { width: w }} />;
}

function Block({ className, w }: { className: string; w?: number }) {
  return <span className={`home-sk-block ${className}`} style={w === undefined ? undefined : { width: w }} />;
}

function HeadSkeleton({ w, right }: { w: number; right?: number }) {
  return (
    <div className="home-head">
      <Sk kind="head" w={w} />
      <span className="home-head-push" />
      {right ? <Sk kind="link" w={right} /> : null}
    </div>
  );
}

const SK_COUNTERS = [110, 58, 51, 103];
const SK_CALLS = [
  [285, 190],
  [325, 150],
  [290, 285],
];
const SK_LATEST = [296, 352, 273, 249, 314];

function HomeSkeleton({ topLane }: { topLane?: ReactNode }) {
  return (
    <div className="home">
      {topLane}
      <div className="home-band" aria-hidden="true">
        <div className="home-band-left">
          <div className="home-words">
            <div className="home-standing">
              <Sk kind="since" w={230} />
              {/* Two lines on a desktop, four on a phone (home.css). */}
              <div className="home-sk-line">
                <Sk kind="line" />
                <Sk kind="line" />
                <Sk kind="line" />
                <Sk kind="line" />
              </div>
            </div>
            <div className="home-counters">
              {SK_COUNTERS.map((w, i) => (
                <div key={i} className="home-counter">
                  <Sk kind="counter" w={i === 3 ? 40 : 26} />
                  <Sk kind="label" w={w} />
                </div>
              ))}
            </div>
          </div>
          <div className="home-command">
            <Block className="home-sk-composer" />
            <div className="home-tray">
              <Block className="home-sk-picker" />
              <span className="home-sk-line">
                <Sk kind="hint" />
                <Sk kind="hint" />
              </span>
            </div>
            <div className="ws-qc-row home-creos">
              <div className="ws-qc-row-head">
                <Sk kind="creos" w={44} />
                <span className="home-head-push" />
                <Sk kind="all" w={56} />
              </div>
              <div className="ws-qc-features">
                {Array.from({ length: 4 }, (_, i) => (
                  <Block key={i} className="home-sk-creo" />
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="home-universe">
          <BrandCoreSkeleton />
          <Sk kind="meta" w={256} />
        </div>
      </div>
      <div className="home-cols" aria-hidden="true">
        <div className="home-col home-col--wide">
          <div className="home-sec">
            <HeadSkeleton w={82} />
            <div className="home-list">
              {SK_CALLS.map(([title, meta], i) => (
                <div key={i} className="home-call">
                  <Block className="home-sk-dot" />
                  <span className="home-call-text">
                    <Sk kind="title" w={title} />
                    <Sk kind="meta" w={meta} />
                  </span>
                  <Block className="home-sk-action" />
                </div>
              ))}
            </div>
          </div>
          <div className="home-sec">
            <HeadSkeleton w={85} right={106} />
            {[395, 60].map((phases, i) => (
              <div key={i} className="home-month">
                <div className="home-month-head home-sk-month-head">
                  <Sk kind="month" w={i === 0 ? 60 : 82} />
                  <Sk kind="title" w={i === 0 ? 92 : 22} />
                  <span className="home-head-push" />
                  <Sk kind="mono" w={i === 0 ? 115 : 60} />
                </div>
                <Sk kind="phases" w={phases} />
                <Block className="home-sk-bar" />
              </div>
            ))}
          </div>
        </div>
        <div className="home-col">
          <div className="home-sec">
            <HeadSkeleton w={186} />
            <div className="home-list">
              {Array.from({ length: 4 }, (_, i) => (
                <div key={i} className="home-learned">
                  <Block className="home-sk-kind" />
                  <span className="home-learned-words home-sk-fill">
                    <span className="home-sk-line">
                      <Sk kind="learned" w="88%" />
                      <Sk kind="learned" w="42%" />
                    </span>
                    <Sk kind="meta" w={i === 0 ? 110 : 96} />
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="home-cols" aria-hidden="true">
        <div className="home-col home-col--wide">
          <div className="home-sec">
            <HeadSkeleton w={51} />
            <div className="home-list">
              {SK_LATEST.map((w, i) => (
                <div key={i} className="home-activity">
                  <Sk kind="age" w={i < 3 ? 26 : 18} />
                  <Block className="home-sk-tick" />
                  <Sk kind="activity" w={w} />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="home-col">
          <div className="home-sec">
            <HeadSkeleton w={143} />
            <div className="home-trend">
              <Block className="home-sk-media" />
              <div className="home-trend-text home-sk-fill">
                <Sk kind="mono" w={165} />
                <span className="home-sk-line">
                  <Sk kind="title" w="96%" />
                  <Sk kind="title" w="36%" />
                </span>
                <span className="home-sk-line">
                  <Sk kind="body" w="90%" />
                  <Sk kind="body" w="48%" />
                </span>
                <Sk kind="src" w={125} />
                <Sk kind="button" w={56} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <p className="co-sr-only" role="status">
        Reading the brand.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------- page */

export default function HomePage({
  data,
  state,
  composer,
  topLane,
}: {
  data: HomeData;
  state?: HomeState;
  composer: ReactNode;
  /** The phone/tablet top lane (menu, title, bell); absent on desktop. */
  topLane?: ReactNode;
}) {
  if (state?.isError) {
    return (
      <div className="home">
        {topLane}
        <section className="home-band home-band--error">
          <div className="home-band-left">
            <p className="home-since">{data.brand.name.toUpperCase()}</p>
            <h1 className="home-line">The month could not be read.</h1>
            <p className="home-quiet">Nothing here is a number to act on until it loads. Try again in a moment.</p>
            {composer}
          </div>
        </section>
      </div>
    );
  }

  if (state?.isLoading) return <HomeSkeleton topLane={topLane} />;

  return <HomeReady data={data} composer={composer} topLane={topLane} />;
}

function HomeReady({ data, composer, topLane }: { data: HomeData; composer: ReactNode; topLane?: ReactNode }) {
  const homeRef = useRef<HTMLDivElement>(null);
  // M01 / M02: the page mounts with its figures in, so it arrives straight away.
  useArrival(homeRef, data.brand.id);
  return (
    <div className="home" ref={homeRef}>
      {topLane}
      <Band data={data} composer={composer} />
      <div className="home-cols">
        <div className="home-col home-col--wide">
          <NeedsYou needs={data.needs} />
          <TheMonth months={data.months} read={data.monthsRead} />
        </div>
        <div className="home-col">
          <Learned rows={data.learned} read={data.learnedRead} />
        </div>
      </div>
      <div className="home-cols">
        <div className="home-col home-col--wide">
          <Latest rows={data.latest} read={data.latestRead} />
        </div>
        <div className="home-col">
          <TrendOfWeek trend={data.trend} read={data.trendRead} />
        </div>
      </div>
    </div>
  );
}
